import { Box, Typography, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { usePumpRecommendationContext } from "../context/PumpRecommendationContext";
import { useEffect } from "react";
import { RecommendationPumpBox } from "../components/ResultStep/RecommendationPumpBox";
import { EmptyRecommendationBox } from "../components/ResultStep/EmptyRecommendationBox";
import { MultipleUnitsBox } from "../components/ResultStep/MultiplePumpsBox";
import { EletricCostGraph } from "../components/EletricCostGraph";
import useResponsive from "../hooks/useResponsive";
import { DisclaimerBox } from "../components/ResultStep/DisclaimerBox";
import { ResultReportBox } from "../components/ResultStep/ResultReportBox";
import { usePDFGeneration } from "../hooks/usePDFGeneration";

export function ResultsPage() {
  const navigate = useNavigate();
  const { recommendations, error } = usePumpRecommendationContext();
  const { isSm, isXs } = useResponsive();
  const { generatePDF, graphRef } = usePDFGeneration();
  const isMobile = isSm || isXs;

  useEffect(() => {
    if (!recommendations) {
      navigate("/");
    }
  }, [recommendations, navigate]);

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h4" color="error" gutterBottom>
          Calculation Error
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/")}>
          Voltar ao Início
        </Button>
      </Box>
    );
  }

  if (!recommendations) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Loading results...</Typography>
      </Box>
    );
  }

  if (recommendations.pumpCounter > 2) {
    return (
      <Box sx={{ p: 4 }}>
        <EmptyRecommendationBox />
      </Box>
    );
  }

  const noRecommendation =
    recommendations.standard.capacity === 0 &&
    recommendations.premium.capacity === 0;

  if (noRecommendation) {
    return (
      <Box sx={{ p: 4 }}>
        <EmptyRecommendationBox />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
        sx={{ mb: 4 }}
      >
        <Box width={"100%"}>
          <Box
            pt={10}
            mt={isMobile ? 0 : 4}
            display={"flex"}
            alignItems={"center"}
            flexDirection={"column"}
            justifyContent={"center"}
            sx={{ width: "100%", background: "#E4E4E4" }}
          >
            <Typography
              width={"75%"}
              fontSize={isMobile ? 20 : 28}
              textAlign="center"
            >
              {`According to the information you provided, our calculation indicates
               that a minimum heat output of ${recommendations.powerRequirements.small.toFixed(
                 2
               )}`}{" "}
              kW @ Air {recommendations.airTemperatureLabel} heat pump is
              required.
            </Typography>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              sx={isMobile ? { width: "300px" } : { width: "100%" }}
            >
              <Stack
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                sx={{ background: "#ffd200" }}
                position={"relative"}
                padding={isMobile ? 4 : 6}
                bottom={-50}
              >
                <Typography
                  fontSize={isMobile ? 28 : 48}
                  fontWeight={"bold"}
                  textAlign="center"
                >
                  Recommended Models*
                </Typography>
                <Typography textAlign="center">
                  Models that match your selections
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box mt={14}>
        {recommendations.pumpCounter === 2 ? (
          <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
            <MultipleUnitsBox
              type="Best Option"
              totalPrice={recommendations.standard.totalPrice}
              annualCOP={recommendations.standard.annualCOP}
              firstHeating={recommendations.standard.firstHeating}
              highestSpeedRate={recommendations.standard.highestSpeedRate}
              pumpModel={recommendations.standard.pumpModel}
              airTemperatureLabel={recommendations.airTemperatureLabel}
            />
          </Box>
        ) : (
          <Stack
            flexDirection={isMobile ? "column" : "row"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={1}
          >
            <RecommendationPumpBox
              type="Option 1"
              totalPrice={recommendations.standard.totalPrice}
              annualCOP={recommendations.standard.annualCOP}
              firstHeating={recommendations.standard.firstHeating}
              highestSpeedRate={recommendations.standard.highestSpeedRate}
              pumpModel={recommendations.standard.pumpModel}
              airTemperatureLabel={recommendations.airTemperatureLabel}
            />
            <RecommendationPumpBox
              type="Option 2"
              totalPrice={recommendations.premium.totalPrice}
              annualCOP={recommendations.premium.annualCOP}
              firstHeating={recommendations.premium.firstHeating}
              highestSpeedRate={recommendations.premium.highestSpeedRate}
              pumpModel={recommendations.premium.pumpModel}
              airTemperatureLabel={recommendations.airTemperatureLabel}
            />
          </Stack>
        )}
      </Box>

      <Box mt={8}>
        <EletricCostGraph recommendations={recommendations} ref={graphRef} />
      </Box>

      <ResultReportBox onGeneratePdf={() => generatePDF(true)} />

      <DisclaimerBox />
    </Box>
  );
}
